<script setup lang="ts">
  import { PhoneNumbersService, type CustomerPhoneNumber as PhoneNumber } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import { TableRow, TableCell } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import { PhoneIcon, ClipboardIcon } from '@heroicons/vue/24/outline'
  import { useTranslation } from '@/composables/useTranslation'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { ChevronRightIcon } from '@heroicons/vue/24/outline'

  const { t } = useTranslation({
    en: {
      phoneNumbers: 'Phone Numbers',
      phoneNumber: 'Phone Number',
      status: 'Status',
      description: 'Description',
      lawFirmName: 'Law Firm Name',
      copy: 'Copy',
      edit: 'Edit',
      active: 'Active',
      inactive: 'Inactive',
      aiPhoneAnswering: 'AI Phone Answering'
    },
    de: {
      phoneNumbers: 'Telefonnummern',
      phoneNumber: 'Telefonnummer',
      status: 'Status',
      description: 'Beschreibung',
      lawFirmName: 'Kanzleiname',
      copy: 'Kopieren',
      edit: 'Bearbeiten',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      aiPhoneAnswering: 'KI-Anrufbeantworter'
    }
  })

  const router = useRouter()

  const fetchPhoneNumbers = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<PhoneNumber>> => {
    return PhoneNumbersService.phoneNumbersList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    }) as Promise<PaginatedResponse<PhoneNumber>>
  }

  const copyPhoneNumberToClipboard = (phoneNumber: string) => {
    navigator.clipboard.writeText(phoneNumber)
  }

  const navigateToDetail = (uuid: string) => {
    router.push(ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS_DETAIL.replace(':uuid', uuid))
  }
</script>

<template>
  <SettingsPage>
    <template #header>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <nav class="flex items-center gap-2 text-sm text-gray-500">
            <div>{{ t('aiPhoneAnswering') }}</div>
            <ChevronRightIcon class="size-3" />
            <span class="font-medium text-gray-900">{{ t('phoneNumbers') }}</span>
          </nav>
        </div>
      </div>
    </template>

    <div class="space-y-4">
      <div>
        <PaginatedTableView
          :fetchData="fetchPhoneNumbers"
          searchableFields="UUID, phone number or description"
        >
          <template #table-head>
            <TableCell class="py-2">{{ t('phoneNumber') }}</TableCell>
            <TableCell class="py-2">{{ t('status') }}</TableCell>
            <TableCell class="py-2">{{ t('description') }}</TableCell>
            <TableCell class="py-2">{{ t('lawFirmName') }}</TableCell>
            <TableCell class="py-2"></TableCell>
          </template>
          <template #table-row="{ items: phoneNumbers }: { items: PhoneNumber[] }">
            <TableRow
              v-for="phoneNumber in phoneNumbers"
              :key="phoneNumber.uuid"
              class="cursor-pointer border-b border-gray-200 hover:bg-gray-50"
              @click="navigateToDetail(phoneNumber.uuid!)"
            >
              <TableCell>
                <div class="flex items-center gap-2">
                  <PhoneIcon class="size-4 text-gray-400" />
                  <span class="font-mono">{{ phoneNumber.phone_number }}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    @click.stop="copyPhoneNumberToClipboard(phoneNumber.phone_number!)"
                  >
                    <ClipboardIcon class="size-4" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <span
                  :class="[
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                    phoneNumber.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  ]"
                >
                  {{ phoneNumber.active ? t('active') : t('inactive') }}
                </span>
              </TableCell>
              <TableCell>
                {{ phoneNumber.description || '-' }}
              </TableCell>
              <TableCell>
                {{ phoneNumber.lawfirm_name || '-' }}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  @click.stop="navigateToDetail(phoneNumber.uuid!)"
                >
                  {{ t('edit') }}
                </Button>
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
  </SettingsPage>
</template>
